import React from 'react';
import { useLinesContext } from '../../../../contexts/Lines';
import {
  stage as stageEnum,
  getStagesInTimeline,
  specialStages,
  lineRowHeight,
  stageTabHeight,
  getOriginLine,
  shouldLineShowAddStageButton,
} from '../../../../utils';
import { Color } from '../../../../themes/styles';
import { ILine } from '../../../../global';
import theme from '../../../../themes/CBB/theme';
import moment, { Moment } from 'moment';
import makeStyles from '@material-ui/core/styles/makeStyles';
import LineDuration from './LineDuration';
import Stage from './Stage';
import Divider from '../../Misc/DividerVertical';
import AddStage from './AddStage';
import cloneDeep from 'lodash.clonedeep';
import CustomTooltip from '../../Tooltip/Tooltip';

const useStyles = makeStyles((theme: any) => ({
  entireLine: {
    height: lineRowHeight - stageTabHeight,
    position: 'relative',
    zIndex: theme.zIndex.pageContent,
  },
  stagesContainer: {
    height: '100%',
    display: 'flex',
    position: 'relative',
    backgroundColor: 'white',
    border: `0.5px solid ${theme.palette.brand.blueGrey}`,
    borderBottomRightRadius: theme.spacing(2),
    borderBottomLeftRadius: theme.spacing(2),
  },
  stageContainer: {
    display: 'flex',
  },
  addStage: { marginLeft: 'auto', display: 'flex' },
}));

function formatDate(date: Moment) {
  return date.format('yyyy-MM');
}

function getAllMonthsInLine(stages: any, timelineInfo: any) {
  const startOfTimeline = formatDate(timelineInfo.start);
  const endOfTimeline = formatDate(timelineInfo.end);
  let startOfLine = formatDate(moment(stages[0].start));
  if (startOfLine < startOfTimeline) {
    startOfLine = startOfTimeline;
  }
  let endOfLine = formatDate(moment(stages[stages.length - 1].start));
  if (endOfLine > endOfTimeline) {
    endOfLine = endOfTimeline;
  }
  const allMonthsInLine = {};
  let currentMonth = startOfLine;
  while (currentMonth <= endOfLine) {
    allMonthsInLine[currentMonth] = [];
    currentMonth = formatDate(moment(currentMonth).add(1, 'month'));
  }
  return allMonthsInLine;
}

function groupStagesByMonth(line: ILine, timelineInfo: any) {
  const lineCopy = line.edit ? cloneDeep(line) : line;
  let stagesGroupedByMonth = getAllMonthsInLine(lineCopy.stages, timelineInfo);
  const stagesInTimeline = getStagesInTimeline(lineCopy, timelineInfo);
  const stagesNoSpecialStages = stagesInTimeline.filter(
    (stage) => !specialStages.includes(stage.name),
  );
  stagesNoSpecialStages.forEach((stage) => {
    if (stage.name !== stageEnum.sellingStart.value) {
      let stageDate = formatDate(moment(stage.start));
      stagesGroupedByMonth[stageDate] = stagesGroupedByMonth[stageDate] || [];
      stagesGroupedByMonth[stageDate].push(stage);
    }
  });
  return Object.values(stagesGroupedByMonth);
}

function groupStagesByMonthOnboarding(line: ILine) {
  let stagesOnboarding = [];
  line.stages.forEach((stage) => {
    if (stage.name !== stageEnum.sellingStart.value) {
      stagesOnboarding.push([stage]);
    }
  });
  return Object.values(stagesOnboarding);
}

export default function Stages({
  line,
  numColumns,
  onboarding = false,
  handleStageUpdate,
  addStage,
  addOrDeleteError,
  exampleStages = false,
  setAutoSave = (arg) => {},
  openStockOnHand,
  setOpenStockOnHand,
}) {
  const classes = useStyles();
  const { timelineInfo, lines } = useLinesContext();
  const originLine = getOriginLine(line, lines);

  line.stages = line?.stages?.filter((stage) => !stage.deleted);
  let stagesGroupedByMonth;
  if (onboarding) {
    stagesGroupedByMonth = groupStagesByMonthOnboarding(line);
  } else {
    stagesGroupedByMonth = groupStagesByMonth(line, timelineInfo);
  }

  return (
    <>
      {stagesGroupedByMonth.length > 0 && (
        <div
          className={classes.entireLine}
          style={{
            width: onboarding ? '80%' : '100%',
          }}
        >
          <div
            className={classes.stagesContainer}
            style={{
              boxShadow:
                line.edit && !onboarding ? theme.boxShadow.main : 'none',
            }}
          >
            {stagesGroupedByMonth.map((stagesInMonth, i) => {
              const isLastStage = stagesGroupedByMonth.length === i + 1;
              return (
                <React.Fragment key={stagesInMonth[0]?.id || i}>
                  <CustomTooltip
                    arrow
                    title={'Example of a supply chain Stage'}
                    placement="bottom-end"
                    open={i === 0 && exampleStages}
                  >
                    <div
                      className={classes.stageContainer}
                      style={{
                        width: 100 / numColumns + (line.edit ? 1 : 0) + '%',
                        opacity: i !== 0 && exampleStages ? '40%' : '100%',
                      }}
                    >
                      <Stage
                        line={line}
                        stages={stagesInMonth}
                        handleStageUpdate={handleStageUpdate}
                        addOrDeleteError={addOrDeleteError}
                        setAutoSave={setAutoSave}
                      />
                      {!isLastStage && (
                        <Divider
                          color={Color.GREY_LIGHT}
                          height="default"
                          marginTop={7}
                          marginBottom={1}
                        />
                      )}
                    </div>
                  </CustomTooltip>
                  {isLastStage &&
                    shouldLineShowAddStageButton(line, onboarding) && (
                      <div className={classes.addStage}>
                        <Divider
                          color={Color.GREY_LIGHT}
                          height="default"
                          marginTop={1}
                          marginBottom={1}
                        />
                        <AddStage
                          addStage={addStage}
                          setAutoSave={setAutoSave}
                          originLine={originLine}
                        />
                      </div>
                    )}
                </React.Fragment>
              );
            })}
          </div>
          <LineDuration
            line={line}
            onboarding={onboarding}
            handleStageUpdate={handleStageUpdate}
            addOrDeleteError={addOrDeleteError}
            setAutoSave={setAutoSave}
            openStockOnHand={openStockOnHand}
            setOpenStockOnHand={setOpenStockOnHand}
          />
        </div>
      )}
    </>
  );
}
